/**
 * Percentiles in both directions: the value at a percentile, and the percentile rank of
 * a value.
 *
 * Value at a percentile, three conventions (Hyndman & Fan 1996 numbering):
 * - `nearest-rank` — type 1, the inverse of the empirical CDF: the value at rank
 *   ⌈P/100 × n⌉. Always one of the data values. NumPy `method="inverted_cdf"`.
 * - `inclusive` — type 7, linear interpolation at position 1 + (n − 1)P/100. Excel
 *   `PERCENTILE.INC`, R's default, NumPy's default.
 * - `exclusive` — type 6, position (n + 1)P/100. Excel `PERCENTILE.EXC`, SPSS. Only
 *   defined for 1/(n + 1) ≤ P/100 ≤ n/(n + 1); Excel returns #NUM! outside that range
 *   and so does this (`value: null`).
 *
 * Percentile rank, three definitions (SciPy `percentileofscore` kinds):
 * - `strict` — percent of values strictly below x.
 * - `weak` — percent at or below x (the empirical CDF).
 * - `mean` — the mid-rank compromise: (below + ½ × equal) / n.
 */

import { sorted } from "@/lib/stats/descriptive";
import {
  type QuantileStep,
  quantilePosition,
  quantileStep,
} from "@/lib/tool-kit/stats-descriptive/quartiles";

export type PercentileMethod = "nearest-rank" | "inclusive" | "exclusive";

export interface PercentileValue {
  method: PercentileMethod;
  percent: number;
  /** Null when the exclusive method cannot reach this percentile with n values. */
  value: number | null;
  /** 1-based rank (nearest-rank) or interpolation position. */
  position: number;
  /** The interpolation, for the two interpolating methods. */
  step: QuantileStep | null;
}

/** Guards ⌈x⌉ against binary noise: 30 × 10 / 100 must be rank 3, not 4. */
const RANK_EPSILON = 1e-9;

export function nearestRank(n: number, percent: number): number {
  return Math.min(
    n,
    Math.max(1, Math.ceil((percent * n) / 100 - RANK_EPSILON))
  );
}

export function percentileValue(
  values: readonly number[],
  percent: number,
  method: PercentileMethod
): PercentileValue | null {
  const data = sorted(values);
  const n = data.length;
  if (n === 0 || percent < 0 || percent > 100) {
    return null;
  }
  if (method === "nearest-rank") {
    const rank = nearestRank(n, percent);
    return {
      method,
      percent,
      value: data[rank - 1],
      position: rank,
      step: null,
    };
  }
  const p = percent / 100;
  const position = quantilePosition(n, p, method);
  if (method === "exclusive" && (position < 1 || position > n)) {
    return { method, percent, value: null, position, step: null };
  }
  const step = quantileStep(data, p, method);
  return { method, percent, value: step.value, position, step };
}

/** The range of percentiles the exclusive method can estimate from n values. */
export function exclusiveRange(n: number): { low: number; high: number } {
  return { low: 100 / (n + 1), high: (100 * n) / (n + 1) };
}

export interface PercentileRank {
  n: number;
  /** Values strictly below x. */
  below: number;
  /** Values equal to x. */
  equal: number;
  /** Percent strictly below, 0–100. */
  strict: number;
  /** Percent at or below, 0–100. */
  weak: number;
  /** Mid-rank percent, 0–100. */
  mean: number;
}

export function percentileRank(
  values: readonly number[],
  x: number
): PercentileRank | null {
  const n = values.length;
  if (n === 0) {
    return null;
  }
  let below = 0;
  let equal = 0;
  for (const value of values) {
    if (value < x) {
      below += 1;
    } else if (value === x) {
      equal += 1;
    }
  }
  return {
    n,
    below,
    equal,
    strict: (100 * below) / n,
    weak: (100 * (below + equal)) / n,
    mean: (100 * (below + equal / 2)) / n,
  };
}
